import { useEffect, useMemo, useState } from 'react';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import {
    financeBillFun,
    financeExportFun,
    financeOptionsFun,
    financeReportFun,
    financeSummaryFun
} from '../../api/supportFinance';
import errorHandle from '../../utils/errorHandle';
import FinanceTable from './FinanceTable';
import TrendChart from './TrendChart';
import { BOOKED_VIA, COLUMNS, PRESETS, TABS, TYPES, TYPE_LABEL, fyOf, presetRange, rupee } from './financeConfig';
import styles from './supFinance.module.css';

/*
 * Support > Finance — sales, money received, refunds, commission, GST, TDS,
 * payouts and the CA pack, for any period / type / organizer / listing.
 * Every number is worked out on the server; this page only shows it.
 */

function Tile({ label, value, note, tone }) {
    return (
        <div className={`${styles.tile} ${tone ? styles[tone] : ''}`}>
            <span className={styles.tileLabel}>{label}</span>
            <strong className={styles.tileValue}>{rupee(value)}</strong>
            {note && <span className={styles.tileNote}>{note}</span>}
        </div>
    );
}

function Overview({ data }) {
    const t = data?.totals || {};
    return (
        <>
            {data?.estimated && (
                <p className={styles.notice}>
                    Some commission is <strong>estimated</strong> from the organizer’s commission % — the exact figure
                    is fixed when the payout is generated.
                </p>
            )}
            {data?.missingGateway > 0 && (
                <p className={styles.notice}>
                    {data.missingGateway} older payment{data.missingGateway === 1 ? ' has' : 's have'} no Razorpay fee
                    details saved, so gateway charges may be a little low.
                </p>
            )}
            <h3 className={styles.groupTitle}>Money in</h3>
            <div className={styles.tiles}>
                <Tile label='Sales (GMV)' value={t.sales} note={`${t.bookings || 0} bookings · full price incl. tax`} />
                <Tile label='Received online' value={t.received} note='paid by customers to us (Razorpay)' />
                <Tile label='Refunds' value={t.refunds} note={`${t.refundCount || 0} cancellations`} tone='bad' />
                <Tile label='Net received' value={t.netReceived} note='received − refunds' tone='strong' />
            </div>

            <h3 className={styles.groupTitle}>Our earnings</h3>
            <div className={styles.tiles}>
                <Tile label='Commission' value={t.commission} note='our income (before GST)' tone='good' />
                <Tile
                    label='GST on commission'
                    value={t.gstOnCommission}
                    note={`${t.gstRate ?? 18}% ${t.gstMode || ''} · payable to govt`}
                />
                <Tile label='Offers & coins we paid' value={t.offers} note='discounts funded by Trvto' tone='bad' />
                <Tile
                    label='Gateway charges'
                    value={t.gatewayFee}
                    note={`+ ${rupee(t.gatewayGst)} GST · ${rupee(t.convenienceFee)} recovered as conv. fee`}
                    tone='bad'
                />
                <Tile
                    label='Net profit'
                    value={t.profit}
                    note='commission − offers − (gateway − conv. fee)'
                    tone='strong'
                />
            </div>

            <h3 className={styles.groupTitle}>Organizers</h3>
            <div className={styles.tiles}>
                <Tile
                    label='Owed for this period'
                    value={t.payable}
                    note={t.tdsDeducted ? 'gross − commission − GST − TDS' : 'gross − commission'}
                />
                <Tile
                    label={t.tdsDeducted ? 'TDS deducted' : 'TDS due (not deducted yet)'}
                    value={t.tds}
                    note={`${t.tdsRate ?? 1}% of organizer gross`}
                    tone={t.tdsDeducted ? '' : 'warn'}
                />
                <Tile label='Paid out in period' value={t.paidOut} note={`${t.payoutCount || 0} payouts`} tone='good' />
                <Tile
                    label='Still to pay (all time)'
                    value={t.pendingPayout}
                    note='generated / not yet paid'
                    tone='warn'
                />
            </div>

            <div className={styles.twoCol}>
                <TrendChart
                    title={`Sales by ${data?.trendUnit || 'day'}`}
                    points={(data?.trend || []).map((p) => ({
                        label: p.label,
                        value: p.sales,
                        sub: `Commission ${rupee(p.commission)}`
                    }))}
                />
                <div className={styles.card}>
                    <h4 className={styles.cardTitle}>By type</h4>
                    <FinanceTable
                        columns={[
                            { key: 'type', label: 'Type', kind: 'type' },
                            { key: 'bookings', label: 'Bookings', kind: 'num', total: true },
                            { key: 'sales', label: 'Sales', kind: 'money', total: true },
                            { key: 'refunds', label: 'Refunds', kind: 'money', total: true },
                            { key: 'commission', label: 'Commission', kind: 'money', total: true }
                        ]}
                        rows={data?.byType || []}
                    />
                </div>
            </div>
        </>
    );
}

export default function SupFinance() {
    const [tab, setTab] = useState('overview');
    const [preset, setPreset] = useState('month');
    const [range, setRange] = useState(() => presetRange('month'));
    const [type, setType] = useState('');
    const [organizerId, setOrganizerId] = useState('');
    const [listingId, setListingId] = useState('');
    const [bookedVia, setBookedVia] = useState('');
    const [options, setOptions] = useState({ organizers: [], listings: [] });
    const [summary, setSummary] = useState(null);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const filters = useMemo(
        () => ({ from: range?.from, to: range?.to, type, organizerId, listingId, bookedVia }),
        [range, type, organizerId, listingId, bookedVia]
    );

    // organizers + listings for the dropdowns
    useEffect(() => {
        financeOptionsFun(type)
            .then((res) => setOptions({ organizers: res?.data?.organizers || [], listings: res?.data?.listings || [] }))
            .catch(() => setOptions({ organizers: [], listings: [] }));
    }, [type]);

    const listings = options.listings.filter((l) => !organizerId || String(l.organizerId) === String(organizerId));

    useEffect(() => {
        if (!range?.from || !range?.to || range.from > range.to) return;
        let gone = false;
        setLoading(true);
        const req =
            tab === 'overview'
                ? financeSummaryFun(filters).then((res) => !gone && setSummary(res?.data))
                : financeReportFun(tab, { ...filters, ...(tab === 'tax' ? fyOf(new Date(range.to)) : {}) }).then(
                      (res) => !gone && setReport({ tab, data: res?.data })
                  );
        req.catch((error) => !gone && errorHandle(error)).finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [tab, filters, range]);

    const choosePreset = (key) => {
        setPreset(key);
        if (key !== 'custom') setRange(presetRange(key));
    };

    const exportExcel = async () => {
        setExporting(true);
        try {
            const res = await financeExportFun(filters);
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `trvto-finance-${filters.from}-to-${filters.to}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            errorHandle(error);
        } finally {
            setExporting(false);
        }
    };

    const openBill = async (row) => {
        // open the window first (popup blockers allow it only straight from the click)
        const win = window.open('', '_blank');
        try {
            const res = await financeBillFun(row.type, row.payoutId);
            if (win) {
                win.document.write(res.data);
                win.document.close();
            }
        } catch (error) {
            win?.close();
            toast.error('Couldn’t open the bill.');
        }
    };

    const data = report?.tab === tab ? report.data : null;

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <h1 className={styles.title}>Finance</h1>
                <button type='button' className={styles.exportBtn} onClick={exportExcel} disabled={exporting}>
                    {exporting ? 'Preparing…' : 'Export Excel (CA pack)'}
                </button>
            </div>

            <div className={styles.filters}>
                <div className={styles.presets} role='group' aria-label='Period'>
                    {PRESETS.map((p) => (
                        <button
                            key={p.key}
                            type='button'
                            className={preset === p.key ? styles.presetOn : styles.preset}
                            onClick={() => choosePreset(p.key)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className={styles.dates}>
                    <input
                        type='date'
                        aria-label='From'
                        value={range?.from || ''}
                        onChange={(e) => {
                            setPreset('custom');
                            setRange((r) => ({ ...r, from: e.target.value }));
                        }}
                    />
                    <span>to</span>
                    <input
                        type='date'
                        aria-label='To'
                        value={range?.to || ''}
                        onChange={(e) => {
                            setPreset('custom');
                            setRange((r) => ({ ...r, to: e.target.value }));
                        }}
                    />
                </div>
                <select
                    aria-label='Type'
                    value={type}
                    onChange={(e) => {
                        setType(e.target.value);
                        setOrganizerId('');
                        setListingId('');
                    }}
                >
                    {TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                            {t.label}
                        </option>
                    ))}
                </select>
                <select
                    aria-label='Organizer'
                    value={organizerId}
                    onChange={(e) => {
                        setOrganizerId(e.target.value);
                        setListingId('');
                    }}
                >
                    <option value=''>All organizers</option>
                    {options.organizers.map((o) => (
                        <option key={`${o.type}-${o._id}`} value={o._id}>
                            {o.name} · {TYPE_LABEL[o.type]}
                        </option>
                    ))}
                </select>
                <select aria-label='Trip / listing' value={listingId} onChange={(e) => setListingId(e.target.value)}>
                    <option value=''>All trips / listings</option>
                    {listings.map((l) => (
                        <option key={l._id} value={l._id}>
                            {l.title}
                        </option>
                    ))}
                </select>
                <select aria-label='Channel' value={bookedVia} onChange={(e) => setBookedVia(e.target.value)}>
                    {BOOKED_VIA.map((b) => (
                        <option key={b.value} value={b.value}>
                            {b.label}
                        </option>
                    ))}
                </select>
            </div>
            {range?.from > range?.to && <p className={styles.errorLine}>“From” date is after the “to” date.</p>}

            <div className={styles.tabs} role='tablist'>
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type='button'
                        role='tab'
                        aria-selected={tab === t.key}
                        className={tab === t.key ? styles.tabOn : styles.tab}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className={styles.body}>
                {loading && (
                    <div className={styles.loading}>
                        <ReactLoading type='spin' color='#006aff' height={36} width={36} />
                    </div>
                )}
                {tab === 'overview' && summary && <Overview data={summary} />}
                {tab === 'organizers' && data && <FinanceTable columns={COLUMNS.organizers} rows={data.rows} />}
                {tab === 'listings' && data && <FinanceTable columns={COLUMNS.listings} rows={data.rows} />}
                {tab === 'payments' && data && <FinanceTable columns={COLUMNS.payments} rows={data.rows} />}
                {tab === 'refunds' && data && <FinanceTable columns={COLUMNS.refunds} rows={data.rows} />}
                {tab === 'payouts' && data && (
                    <>
                        <h3 className={styles.groupTitle}>Paid in this period</h3>
                        <FinanceTable columns={COLUMNS.payoutsPaid} rows={data.paid} onBill={openBill} />
                        <h3 className={styles.groupTitle}>Still to pay (all time)</h3>
                        <FinanceTable
                            columns={COLUMNS.payoutsPending}
                            rows={data.pending}
                            empty='Nothing waiting to be paid.'
                        />
                    </>
                )}
                {tab === 'tax' && data && (
                    <>
                        <p className={styles.notice}>
                            {data.fy} · GST {data.gstRate}% on commission ({data.gstMode}) · TDS {data.tdsRate}% on
                            organizer gross{data.tdsDeducted ? '' : ' (due — payouts don’t deduct it yet)'}. Rates are
                            settings — confirm them with your CA.
                        </p>
                        <FinanceTable columns={COLUMNS.taxMonths} rows={data.months} />
                        <h3 className={styles.groupTitle}>
                            Organizers without GSTIN ({data.missingGstin?.length || 0})
                        </h3>
                        <FinanceTable
                            columns={[
                                { key: 'name', label: 'Organizer' },
                                { key: 'type', label: 'Type', kind: 'type' },
                                { key: 'sales', label: 'Sales this FY', kind: 'money', total: true },
                                { key: 'phone', label: 'Phone' }
                            ]}
                            rows={data.missingGstin}
                            empty='Every organizer with sales has a GSTIN.'
                        />
                    </>
                )}
            </div>
        </div>
    );
}
